import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Route,
  RouterStateSnapshot,
  UrlSegment
} from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { locales } from '../constants/Locales';

/**
 * AuthGuard for protecting routes
 * Overridden from source -https://github.com/auth0/auth0-angular/blob/master/projects/auth0-angular/src/lib/auth.guard.ts
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  constructor(private auth: AuthService) {}

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    return this.auth.isAuthenticated$.pipe(take(1));
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.redirectIfUnauthenticated(state);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.redirectIfUnauthenticated(state);
  }

  private removeSiteSwitchQueryParams(url: string): string {
    const [base, params] = url.split('?');
    const allowedParams = params
      ?.split('&')
      .filter((param) => !['org_id', 'site_id'].includes(param?.split('=')[0]))
      .join('&');

    return `${base}${allowedParams?.length ? '?' + allowedParams : ''}`;
  }

  private redirectIfUnauthenticated(
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.auth.isAuthenticated$.pipe(
      tap((loggedIn) => {
        const firstUrlPathSegment = window.location.pathname.split('/')[1];

        let redirectUri = window.location.origin;

        if (locales.indexOf(firstUrlPathSegment) > -1) {
          redirectUri += `/${firstUrlPathSegment}`;
        }

        const isInviteLink =
          state.url.includes('invitation') && state.url.includes('organization')
            ? true
            : false;
        const isRedirectLink =
          state.url.includes('org_id') && state.url.includes('site_id')
            ? true
            : false;

        if (isInviteLink) {
          this.auth.loginWithRedirect({
            authorizationParams: {
              invitation: state.url.split('invitation=')[1].split('&')[0],
              organization: state.url.split('organization=')[1].split('&')[0]
            },
            appState: {
              target: state.url.split('?')[0]
            }
          });
        } else if (isRedirectLink) {
          this.auth.loginWithRedirect({
            authorizationParams: {
              redirect_uri: redirectUri,
              organization: state.url?.split('org_id=')[1]?.split('&')[0],
              requestedSiteId: state.url?.split('site_id=')[1]?.split('&')[0]
            },
            appState: {
              target: this.removeSiteSwitchQueryParams(state.url)
            }
          });
        } else if (!loggedIn) {
          this.auth.loginWithRedirect({
            appState: {
              target: state.url
            }
          });
        }
      })
    );
  }
}
